import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useMe } from '@/lib/queries.ts';
import { Accounts } from './Accounts.tsx';
import { Providers } from './Providers.tsx';
import { Lineup } from './Lineup.tsx';
import { Rules } from './Rules.tsx';
import { Guide } from './Guide.tsx';
import { ServerPanel } from './ServerPanel.tsx';

/**
 * THE DESK SIDE OF THE APP.
 *
 * One guard, five screens under it. The split between what lives here and
 * what lives on the television is by INPUT COST rather than by client: this
 * is everything that is a sentence — a hostname, a password, a channel name,
 * a number range — and the remote gets the things that are a press. Same API
 * underneath either way, because the alternative is two APIs, and two is how
 * they come to disagree.
 *
 * TABS ARE ROUTES, not state. It costs one line in App.tsx and buys three
 * things that state cannot: the back button works, a half-finished import is
 * a link somebody can send themselves, and a reload lands where you were
 * rather than back on Accounts.
 */
export function AdminPage() {
  const { data: me } = useMe();

  /*
   * The guard is HERE and nowhere else. Each panel would be five copies of
   * one rule, and five is how one of them ends up subtly different. The
   * server refuses every one of these routes independently anyway — this is
   * about not showing somebody a screen that cannot work.
   */
  if (!me?.user.can.admin) {
    return (
      <div className="center">
        <div className="panel">
          <h1>Not available</h1>
          <p className="muted">This account cannot administer the server.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <nav className="admin-tabs" data-nav-zone="admin-tabs">
        {/* `end` on the index, or it stays lit on every other tab. */}
        <NavLink to="/admin" end className={tab}>Accounts</NavLink>
        <NavLink to="/admin/providers" className={tab}>Providers</NavLink>
        <NavLink to="/admin/lineup" className={tab}>Lineup</NavLink>
        <NavLink to="/admin/rules" className={tab}>Sync rules</NavLink>
        <NavLink to="/admin/guide" className={tab}>Guide</NavLink>
        <NavLink to="/admin/server" className={tab}>Server</NavLink>
      </nav>

      <Routes>
        <Route index element={<Accounts />} />
        <Route path="providers" element={<Providers />} />
        <Route path="lineup" element={<Lineup />} />
        <Route path="rules" element={<Rules />} />
        <Route path="guide" element={<Guide />} />
        <Route path="server" element={<ServerPanel />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </>
  );
}

function tab({ isActive }: { isActive: boolean }) {
  return isActive ? 'admin-tab is-active' : 'admin-tab';
}
